/** The bookable coworking floors. Desk ids are only unique within a space. */

import spaceDefinitions from './spaces-data.json';

export type SpaceId = 'ground' | 'upper';

export interface DeskDef {
  /** Unique within the floor: the artwork's data-desk-id and the desk's stored key, e.g. "FA3". */
  id: string;
  /** The run or bench the desk stands in, e.g. "FA". Upstairs, numbering restarts in each one. */
  cluster: string;
}

export interface SpaceDef {
  id: SpaceId;
  name: string;
  zone: string;
  seats: number;
  /** The floor's desks, in listing order. */
  desks: DeskDef[];
  /** Upper floor only: ENTRANCE / KITCHEN / MEETING ROOM markers. */
  hasPins: boolean;
  room: string;
  /** What the artwork calls its desk groupings, and their range. */
  groupsLabel: string;
  groups: string;
}

/** The two bookable coworking floors, in picker order. */
export const SPACES: readonly SpaceDef[] = spaceDefinitions.map((definition) => ({
  ...definition,
  id: definition.id as SpaceId,
  seats: definition.desks.length,
}));

/** Look a floor up by id, falling back to the first rather than returning undefined. */
export function spaceById(id: string | undefined): SpaceDef {
  return SPACES.find((s) => s.id === id) ?? SPACES[0];
}

/** Desk ids are compared upper-case and trimmed, wherever they came from. */
export const normaliseDeskId = (deskId: string) => String(deskId).trim().toUpperCase();

/** A floor's desk by id, with its cluster, or undefined when the floor has no such desk. */
export function findDesk(space: SpaceDef, deskId: string): DeskDef | undefined {
  const id = normaliseDeskId(deskId);
  return space.desks.find((desk) => desk.id === id);
}

/** Which space a desk id belongs to, looked up in each floor's desk list. */
export function spaceOfDesk(deskId: string): SpaceId | null {
  return SPACES.find((s) => findDesk(s, deskId))?.id ?? null;
}
